import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { colors2, DropdownSingle, SkeletonLoader } from "oolib";
import { UI_BODY_SEMIBOLD, UI_BODY_SEMIBOLD_SM_DF, UI_CAPTION, UI_CAPTION_DF, UI_CAPTION_SEMIBOLD_DF, UI_TAG } from "oolib/dist/v2/components/Typo2";
import { ArrowRight, CaretDown } from "@phosphor-icons/react";
import { SidePanel } from "@/components/UI/SidePanel";
import { useGetTopicClusteringTreeCounts } from "@/utils/react-query-hooks/aiAnnotation";
import { useGetAnnotationsLazy } from "@/utils/react-query-hooks/getData";
import { useAggrMetricsAcrossDimensions } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/WidgetBarChart/queries/useAggrMetricsAcrossDimensions";
import { SentimentBar } from "@/_self_managed/SM_Dashboard/LensesV2/comps/SentimentBar";
import { DatasetCircles } from "@/components/analysisModules/TopicClusteringTreeView/DatasetCircles";
import { RowAnnosPanelBody } from "@/components/analysisModules/TopicClusteringTreeView";
import { useSignalCategories } from "@/components/analysisModules/TopicClusteringTreeView/SignalCategoriesContext";
import { toneColor } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/format";
import { ToolbarSelectTrigger } from "./PeriodPicker";
import { activeFiltersFor, periodFilter, signalsFilter, topicNodeFilter } from "./scopeFilters";
import { TopicQuickSummary } from "@/components/analysisModules/TopicClusteringTreeView/TopicQuickSummary";
import { periodKey } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/periodKeys";

/**
 * One signal category in the period, in a side panel — what a row of the
 * Overview's categories widget opens. The first level of the category
 * stack; "Open topic" on a topic opens its drilldown beside it (onOpenTopic).
 *
 * Top: the category's observations, their share of all, and the change in
 * points since the comparison period; then its signals as filter chips,
 * each with its share of the category's observations. Below: the topics
 * holding those observations, largest first (or by change, or by name),
 * each with its share of the category; the open one also lists its
 * sub-topics and one example observation.
 *
 * Owns its requests: the category's totals for the period and for the
 * comparison period (the metrics engine's `signalCategory` dimension — the
 * same calls the categories widget makes, so they come from cache), the
 * per-signal split (a tag dimension over the category's signals), the tree
 * counts over the scope for the topics (and over the comparison period, for
 * the badges), and one annotation for the open topic.
 *
 *   isOpen, width
 *   category             the category's value
 *   scope                { period, comparePeriod, extraFilters, topicClusteringLens, dateFieldMap }
 *                        — extraFilters narrows to the category's signals
 *   titleByContentType, onOpenTopic(nodeId)
 *   …shell               the SidePanelStack's props for this level
 *
 * Must render inside a SignalCategoriesProvider (reads the theme).
 */
const CATEGORY_DIMENSION = { dimensionType: "signalCategory" };
const SIGNAL_DIMENSION = { valuePath: "annotations.tags.tags" };
const TOPICS_SHOWN = 4;
const SORTS = [
  { value: "count", display: "Most observations" },
  { value: "change", display: "Biggest change" },
  { value: "name", display: "A to Z" },
];
// A topic's move since the comparison period, as a badge.
const badgeFor = (now, then, hasThen) => {
  if (!hasThen) return null;
  if (!then) return { label: "New", tone: "up" };
  const rel = (now - then) / then;
  if (rel >= 0.2) return { label: "Rising", tone: "up" };
  if (rel <= -0.2) return { label: "Cooling", tone: "down" };
  return { label: "Steady", tone: "flat" };
};

const Head = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 14px 14px;
`;
const Dot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
  margin-right: 10px;
  vertical-align: middle;
`;
const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
`;
const Tile = styled.div`
  background: ${colors2.grey2};
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid ${({ $active }) => ($active ? colors2.grey80 : colors2.grey10)};
  background: ${({ $active }) => ($active ? colors2.grey80 : colors2.white)};
  color: ${({ $active }) => ($active ? colors2.white : colors2.grey80)};
  cursor: pointer;
  font: inherit;
`;
const ListHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid ${colors2.grey5};
`;
const TopicRow = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${colors2.grey5};
  background: ${({ $open }) => ($open ? colors2.grey2 : colors2.white)};
  cursor: pointer;
`;
const TopicHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const Badge = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ $tone }) => ($tone === "flat" ? colors2.grey5 : `${toneColor($tone)}1a`)};
  color: ${({ $tone }) => ($tone === "flat" ? colors2.grey60 : toneColor($tone))};
  white-space: nowrap;
`;
const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  & > :first-child {
    flex: 1;
    min-width: 0;
  }
`;
const SubRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 3px 0;
`;
const OpenTopic = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  margin-top: 12px;
  cursor: pointer;
  color: ${colors2.primaryColor100};
  font: inherit;
`;
const More = styled.button`
  background: none;
  border: none;
  padding: 14px 20px;
  cursor: pointer;
  text-align: left;
  font: inherit;
`;
const Section = styled(UI_TAG)`
  display: block;
  color: ${colors2.grey60};
  margin: 14px 0 6px;
`;

const signed = (n) => `${n > 0 ? "+" : n < 0 ? "−" : ""}${Math.abs(n).toFixed(1)}`;
const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

// The open topic's example: its first observation in the scope.
const ExampleObservation = ({ topicNodeId, activeFilters, topicClusteringLens }) => {
  const filters = useMemo(() => [...activeFilters, topicNodeFilter(String(topicNodeId), topicClusteringLens)], [activeFilters, topicNodeId, topicClusteringLens]);
  const query = useGetAnnotationsLazy({ activeFilters: filters, queryId: `categoryPanel:example:${topicNodeId}`, limit: 1, queryOptions: { enabled: !!topicNodeId } });
  const annos = useMemo(() => (query.data?.pages || []).flatMap((pg) => pg.data || []).slice(0, 1), [query.data]);
  return (
    <>
      <Section>Example observation</Section>
      {query.isLoading ? <SkeletonLoader style={{ height: 72, borderRadius: 8 }} /> : <RowAnnosPanelBody annos={annos} status={query.status} />}
    </>
  );
};

export const CategoryTopicsPanel = ({ isOpen, width, category, scope, titleByContentType, onOpenTopic, ...shell }) => {
  const { period, comparePeriod, extraFilters, topicClusteringLens } = scope;
  const categoryTheme = useSignalCategories();
  const cat = categoryTheme.list.find((c) => c.value === category);
  const accent = categoryTheme.accentOf(category);
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [sort, setSort] = useState("count");
  const [openTopicId, setOpenTopicId] = useState(null);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    setSelectedSignal(null);
    setOpenTopicId(null);
    setShowAll(false);
  }, [category]);

  // ── the category's totals, now and then ──
  const { data: catNow } = useAggrMetricsAcrossDimensions({ contentType: "annotations", activeFilters: [periodFilter(period)].filter(Boolean), dimensions: [CATEGORY_DIMENSION], returnMetadata: true, queryOptions: { enabled: isOpen && !!period } });
  const { data: catThen } = useAggrMetricsAcrossDimensions({ contentType: "annotations", activeFilters: [periodFilter(comparePeriod)].filter(Boolean), dimensions: [CATEGORY_DIMENSION], returnMetadata: true, queryOptions: { enabled: isOpen && !!comparePeriod } });
  const now = (catNow?.data || []).find((r) => r.value === category);
  const then = (catThen?.data || []).find((r) => r.value === category);
  const hasThen = !!comparePeriod && catThen?.metadata?.totalDocsInScope > 0;
  const moved = hasThen ? (now?.percentage || 0) - (then?.percentage || 0) : null;
  const categoryCount = now?.count || 0;

  // ── the category's signals, as chips ──
  const categoryFilters = useMemo(() => activeFiltersFor(period, extraFilters), [period, extraFilters]);
  const { data: bySignal } = useAggrMetricsAcrossDimensions({ contentType: "annotations", activeFilters: categoryFilters, dimensions: [SIGNAL_DIMENSION], queryOptions: { enabled: isOpen && !!period } });
  const signals = useMemo(() => (bySignal?.data || bySignal || []).filter((r) => r.count > 0).sort((a, b) => b.count - a.count), [bySignal]);

  // ── the topics, over the scope (the chip narrows it further) ──
  const activeFilters = useMemo(() => (selectedSignal ? [...categoryFilters, signalsFilter([selectedSignal])] : categoryFilters), [categoryFilters, selectedSignal]);
  const { data: counts, isLoading } = useGetTopicClusteringTreeCounts({ topicClusteringLens, activeFilters, enabled: isOpen && !!period });
  const thenFilters = useMemo(() => (comparePeriod ? activeFiltersFor(comparePeriod, selectedSignal ? [...extraFilters, signalsFilter([selectedSignal])] : extraFilters) : null), [comparePeriod, extraFilters, selectedSignal]);
  const { data: prevCounts } = useGetTopicClusteringTreeCounts({ topicClusteringLens, activeFilters: thenFilters || [], enabled: isOpen && !!thenFilters });
  const denominator = selectedSignal ? signals.find((s) => s.value === selectedSignal)?.count || 0 : categoryCount;
  const topics = useMemo(() => {
    const prevByNode = Object.fromEntries((prevCounts?.tree || []).map((t) => [String(t.nodeId), t.annotationCount || 0]));
    const rows = (counts?.tree || [])
      .filter((t) => t.annotationCount > 0)
      .map((t) => {
        const prev = prevByNode[String(t.nodeId)] || 0;
        return {
          nodeId: String(t.nodeId),
          label: t.label,
          description: t.description,
          count: t.annotationCount,
          share: denominator ? (t.annotationCount / denominator) * 100 : 0,
          change: t.annotationCount - prev,
          badge: badgeFor(t.annotationCount, prev, !!prevCounts),
          datasets: t.datasets || [],
          children: (t.children || []).filter((c) => c.annotationCount > 0).sort((a, b) => b.annotationCount - a.annotationCount),
        };
      });
    if (sort === "name") rows.sort((a, b) => a.label.localeCompare(b.label));
    else if (sort === "change") rows.sort((a, b) => Math.abs(b.change) - Math.abs(a.change) || b.count - a.count);
    else rows.sort((a, b) => b.count - a.count);
    return rows;
  }, [counts, prevCounts, denominator, sort]);
  // The first topic opens by itself; a click opens another (or closes it).
  const openId = openTopicId ?? topics[0]?.nodeId ?? null;
  const shown = showAll ? topics : topics.slice(0, TOPICS_SHOWN);
  const hidden = topics.length - shown.length;

  return (
    <SidePanel
      {...shell}
      isOpen={isOpen}
      width={width}
      title={<UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.grey60 }}>{`Signal category · ${period?.display || ""}${comparePeriod ? ` vs ${comparePeriod.display}` : ""}`}</UI_CAPTION_SEMIBOLD_DF>}
      subHeader={
        <Head>
          <div>
            <UI_BODY_SEMIBOLD style={{ color: colors2.grey80 }}>
              <Dot $color={accent} />
              {cat?.display || category}
            </UI_BODY_SEMIBOLD>
            {cat?.description && <UI_CAPTION style={{ color: colors2.grey60, display: "block", marginTop: 4 }}>{cat.description}</UI_CAPTION>}
          </div>
          <Tiles>
            <Tile>
              <UI_CAPTION style={{ color: colors2.grey60 }}>Observations</UI_CAPTION>
              <UI_BODY_SEMIBOLD style={{ color: colors2.grey80 }}>{categoryCount.toLocaleString()}</UI_BODY_SEMIBOLD>
            </Tile>
            <Tile>
              <UI_CAPTION style={{ color: colors2.grey60 }}>Share of all</UI_CAPTION>
              <UI_BODY_SEMIBOLD style={{ color: colors2.grey80 }}>{Math.round(now?.percentage || 0)}%</UI_BODY_SEMIBOLD>
            </Tile>
            <Tile>
              <UI_CAPTION style={{ color: colors2.grey60 }}>Change</UI_CAPTION>
              <UI_BODY_SEMIBOLD style={{ color: colors2.grey80 }}>{moved === null ? "–" : `${signed(moved)} pts`}</UI_BODY_SEMIBOLD>
            </Tile>
          </Tiles>
          {signals.length > 0 && (
            <div>
              <Section style={{ margin: "0 0 8px" }}>Filter by type</Section>
              <Chips>
                <Chip $active={!selectedSignal} onClick={() => setSelectedSignal(null)}>
                  <UI_CAPTION_SEMIBOLD_DF style={{ color: "inherit" }}>All</UI_CAPTION_SEMIBOLD_DF>
                </Chip>
                {signals.map((s) => (
                  <Chip key={s.value} $active={selectedSignal === s.value} onClick={() => setSelectedSignal((v) => (v === s.value ? null : s.value))}>
                    <Dot $color={accent} style={{ marginRight: 0, opacity: selectedSignal === s.value ? 1 : 0.7 }} />
                    <UI_CAPTION_SEMIBOLD_DF style={{ color: "inherit" }}>{s.display}</UI_CAPTION_SEMIBOLD_DF>
                    <UI_CAPTION_DF style={{ color: "inherit", opacity: 0.7 }}>{categoryCount ? `${Math.round((s.count / categoryCount) * 100)}%` : ""}</UI_CAPTION_DF>
                  </Chip>
                ))}
              </Chips>
            </div>
          )}
        </Head>
      }
      bodyStyle={{ padding: 0 }}
    >
      {/* The quick summary of the category: the server narrows to the
          category's signals itself, so only the period and a chosen signal
          travel as filters. */}
      <div style={{ padding: "0 20px" }}>
        <TopicQuickSummary
          nodeType="category"
          nodeId={category}
          scope={{
            activeFilters: activeFiltersFor(period, selectedSignal ? [signalsFilter([selectedSignal])] : []),
            topicClusteringLens,
            period,
            quickSummary: { compareKey: comparePeriod ? periodKey(comparePeriod) : null },
          }}
        />
      </div>
      <ListHead>
        <UI_BODY_SEMIBOLD_SM_DF style={{ color: colors2.grey80 }}>{isLoading ? "Topics" : `${topics.length} topic${topics.length === 1 ? "" : "s"}`}</UI_BODY_SEMIBOLD_SM_DF>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <UI_CAPTION style={{ color: colors2.grey60 }}>Sort</UI_CAPTION>
          <DropdownSingle
            id="categoryPanelSort"
            value={sort}
            options={SORTS}
            onChange={(_k, v) => setSort(v?.value ?? v)}
            saveValueAsString
            popOutOfOverflowHiddenParent
            lightboxStyle={{ minWidth: "16rem" }}
            genCustomSelectComp={() => (
              <ToolbarSelectTrigger>
                <UI_TAG style={{ color: colors2.grey80 }}>{SORTS.find((s) => s.value === sort)?.display}</UI_TAG>
                <CaretDown size={10} weight="bold" color={colors2.grey60} />
              </ToolbarSelectTrigger>
            )}
          />
        </div>
      </ListHead>
      {isLoading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 20 }}>
          {[0, 1, 2].map((i) => (
            <SkeletonLoader key={i} style={{ height: 96, borderRadius: 8 }} />
          ))}
        </div>
      )}
      {!isLoading && topics.length === 0 && (
        <UI_CAPTION style={{ color: colors2.grey60, display: "block", padding: 20 }}>No topics in this category for {period?.display || "this period"}.</UI_CAPTION>
      )}
      {!isLoading &&
        shown.map((topic) => {
          const open = topic.nodeId === openId;
          return (
            <TopicRow key={topic.nodeId} $open={open} onClick={() => setOpenTopicId(open ? "" : topic.nodeId)}>
              <TopicHead>
                <UI_BODY_SEMIBOLD_SM_DF style={{ color: colors2.grey80, flex: 1, minWidth: 0, ...ellipsis }}>{topic.label}</UI_BODY_SEMIBOLD_SM_DF>
                {topic.badge && (
                  <Badge $tone={topic.badge.tone}>
                    <UI_CAPTION_SEMIBOLD_DF style={{ color: "inherit" }}>{topic.badge.label}</UI_CAPTION_SEMIBOLD_DF>
                  </Badge>
                )}
                <DatasetCircles datasets={topic.datasets} titleByContentType={titleByContentType} />
              </TopicHead>
              {topic.description && <UI_CAPTION style={{ color: colors2.grey60, display: "block", marginTop: 4, ...ellipsis }}>{topic.description}</UI_CAPTION>}
              <BarRow>
                <SentimentBar segments={[{ key: topic.nodeId, title: topic.label, value: Math.min(100, topic.share), color: accent }]} height={6} track roundedEnds />
                <UI_CAPTION_DF style={{ color: colors2.grey60, whiteSpace: "nowrap" }}>{`${topic.count} obs · ${Math.round(topic.share)}%`}</UI_CAPTION_DF>
              </BarRow>
              {open && (
                <div onClick={(e) => e.stopPropagation()}>
                  {topic.children.length > 0 && (
                    <>
                      <Section>Sub-topics</Section>
                      {topic.children.slice(0, 3).map((c) => (
                        <SubRow key={String(c.nodeId)}>
                          <UI_CAPTION_DF style={{ color: colors2.grey80, ...ellipsis }}>{c.label}</UI_CAPTION_DF>
                          <UI_CAPTION_DF style={{ color: colors2.grey60, whiteSpace: "nowrap" }}>{c.annotationCount} obs</UI_CAPTION_DF>
                        </SubRow>
                      ))}
                    </>
                  )}
                  <ExampleObservation topicNodeId={topic.nodeId} activeFilters={activeFilters} topicClusteringLens={topicClusteringLens} />
                  <OpenTopic onClick={() => onOpenTopic(topic.nodeId)}>
                    <UI_CAPTION_SEMIBOLD_DF style={{ color: "inherit" }}>Open topic</UI_CAPTION_SEMIBOLD_DF>
                    <ArrowRight size={12} weight="bold" />
                  </OpenTopic>
                </div>
              )}
            </TopicRow>
          );
        })}
      {!isLoading && hidden > 0 && (
        <More onClick={() => setShowAll(true)}>
          <UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.grey60 }}>{`+ ${hidden} more topic${hidden === 1 ? "" : "s"}`}</UI_CAPTION_SEMIBOLD_DF>
        </More>
      )}
    </SidePanel>
  );
};
